import React, { useMemo } from 'react';
import { Card, Row, Col, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../common/Flex';

const BudgetForecast = ({ orders, assignments, clients }) => {
  const forecastData = useMemo(() => {
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const endOfQuarter = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
    
    // Calcular totales
    const totalBudget = orders.reduce((sum, order) => sum + (parseFloat(order.netInvestment) || 0), 0);
    const totalSpent = assignments.reduce((sum, assignment) => {
      let amount = parseFloat(assignment.montoValor) || 0;
      if (assignment.amountType === 'percentage') {
        const order = orders.find(o => o.id === assignment.insertionOrderId);
        if (order && order.netInvestment) {
          amount = (amount * parseFloat(order.netInvestment)) / 100;
        }
      }
      return sum + amount;
    }, 0);

    // Pronóstico para el mes
    const monthlyOrders = orders.filter(order => {
      const orderStart = new Date(order.dateFrom);
      const orderEnd = new Date(order.dateTo);
      return orderStart <= endOfMonth && orderEnd >= now;
    });

    const monthlyBudget = monthlyOrders.reduce((sum, order) => sum + (parseFloat(order.netInvestment) || 0), 0);
    
    // Pronóstico para el trimestre
    const quarterlyOrders = orders.filter(order => {
      const orderStart = new Date(order.dateFrom);
      const orderEnd = new Date(order.dateTo);
      return orderStart <= endOfQuarter && orderEnd >= now;
    });

    const quarterlyBudget = quarterlyOrders.reduce((sum, order) => sum + (parseFloat(order.netInvestment) || 0), 0);

    return {
      totalBudget,
      totalSpent,
      remaining: totalBudget - totalSpent,
      utilizationRate: totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0,
      monthlyBudget,
      quarterlyBudget,
      monthlyOrders: monthlyOrders.length,
      quarterlyOrders: quarterlyOrders.length
    };
  }, [orders, assignments]);

  return (
    <Card className="h-100">
      <Card.Header>
        <Flex justifyContent="between" alignItems="center">
          <h6 className="mb-0">Pronóstico de Presupuesto</h6>
          <FontAwesomeIcon icon="chart-line" className="text-info" />
        </Flex>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={6}>
            <div className="border rounded p-3 h-100">
              <h6 className="text-primary mb-3">
                <FontAwesomeIcon icon="calendar-day" className="me-2" />
                Este Mes
              </h6>
              <h4 className="text-primary mb-1">${(forecastData.monthlyBudget / 1000).toFixed(1)}K</h4>
              <p className="text-muted fs-11 mb-2">{forecastData.monthlyOrders} órdenes activas</p>
              <ProgressBar 
                now={forecastData.utilizationRate} 
                variant={forecastData.utilizationRate > 90 ? 'danger' : forecastData.utilizationRate > 70 ? 'warning' : 'success'}
                className="mb-2"
                style={{ height: '6px' }}
              />
              <small className="text-muted">Utilización: {forecastData.utilizationRate.toFixed(1)}%</small>
            </div>
          </Col>
          
          <Col md={6}>
            <div className="border rounded p-3 h-100">
              <h6 className="text-info mb-3">
                <FontAwesomeIcon icon="calendar-week" className="me-2" />
                Este Trimestre
              </h6>
              <h4 className="text-info mb-1">${(forecastData.quarterlyBudget / 1000).toFixed(1)}K</h4>
              <p className="text-muted fs-11 mb-2">{forecastData.quarterlyOrders} órdenes planificadas</p>
              <div className="d-flex justify-content-between fs-11 text-muted">
                <span>Gastado: ${(forecastData.totalSpent / 1000).toFixed(1)}K</span>
                <span>Restante: ${(forecastData.remaining / 1000).toFixed(1)}K</span>
              </div>
            </div>
          </Col>
        </Row>
        
        <div className="mt-4">
          <h6 className="mb-3">Resumen General</h6>
          <Row className="g-2">
            <Col xs={6} sm={3}>
              <div className="text-center p-2 bg-light rounded">
                <h5 className="mb-1 text-success">${(forecastData.totalBudget / 1000).toFixed(0)}K</h5>
                <small className="text-muted">Total Presupuesto</small>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="text-center p-2 bg-light rounded">
                <h5 className="mb-1 text-primary">${(forecastData.totalSpent / 1000).toFixed(0)}K</h5>
                <small className="text-muted">Total Gastado</small>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="text-center p-2 bg-light rounded">
                <h5 className="mb-1 text-warning">${(forecastData.remaining / 1000).toFixed(0)}K</h5>
                <small className="text-muted">Disponible</small>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="text-center p-2 bg-light rounded">
                <h5 className="mb-1 text-info">{forecastData.utilizationRate.toFixed(0)}%</h5>
                <small className="text-muted">Utilización</small>
              </div>
            </Col>
          </Row>
        </div>
      </Card.Body>
    </Card>
  );
};

export default BudgetForecast; 